import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Documents.css";
import Breadcrumb from "./Breadcrumb";
import ClientDocumentsSection from "./ClientDocumentsSection";
import { useAuth } from "../context/AuthContext";
import { useMatter } from "../context/MatterContext";
import {
  fetchClientRetainers,
  downloadClientRetainer,
} from "../helpers/apiHelper";
import { formatCreatedDate, formatFileSize } from "../helpers/utilityHelper";

const sanitizeFilename = (name) =>
  String(name || "")
    .replace(/[\\/:*?"<>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ensurePdfExtension = (name) => {
  const normalized = sanitizeFilename(name) || "Contrato de Retenção";
  return normalized.toLowerCase().endsWith(".pdf")
    ? normalized
    : `${normalized}.pdf`;
};

export default function Documents() {
  const navigate = useNavigate();
  const { tokens } = useAuth();
  const { party, matter } = useMatter();

  const [retainerError, setRetainerError] = useState("");
  const [retainerFiles, setRetainerFiles] = useState([]);
  const [retainerFilesLoading, setRetainerFilesLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState("");

  const activeMatter = useMemo(
    () => (matter && typeof matter === "object" ? matter : null),
    [matter],
  );

  const docs = useMemo(
    () => [
      {
        id: "doc-terms",
        title: "Termos de Prestação de Serviço",
        url: "https://s3.eu-west-2.amazonaws.com/assets.pogustgoodhead.com/projectr/index.html?token=e7f43dd4-6273-41db-9d6d-1c67c3aecbd1&locale=en_GB",
      },
      {
        id: "doc-customer-service-letter",
        title: "Carta do serviço de atendimento ao cliente",
        url: "https://s3.eu-west-2.amazonaws.com/assets.pogustgoodhead.com/projectr/index.html?token=7543b879-3413-40f2-a320-b854927ecd9e&locale=en_GB",
      },
      {
        id: "doc-plain-language-summary",
        title: "Resumo em linguagem simples",
        url: "https://s3.eu-west-2.amazonaws.com/assets.pogustgoodhead.com/projectr/index.html?token=05970baa-9da4-44a7-b586-b5515550b508&locale=en_GB",
      },
      {
        id: "doc-lma",
        title: "Contrato de Gestão do Litígio (LMA)",
        url: "https://s3.eu-west-2.amazonaws.com/assets.pogustgoodhead.com/projectr/index.html?token=ac1ecb34-937e-459b-a65f-1cc51499e9db&locale=en_GB",
      },
    ],
    [],
  );

  const openDoc = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getToken = useCallback(
    () =>
      tokens?.idToken ||
      tokens?.accessToken ||
      localStorage.getItem("idToken") ||
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("idToken") ||
      sessionStorage.getItem("accessToken") ||
      "",
    [tokens],
  );

  const getPartyId = useCallback(
    () =>
      party?.id ||
      party?.partyId ||
      party?.Id ||
      localStorage.getItem("clientId") ||
      sessionStorage.getItem("clientId") ||
      "",
    [party],
  );

  const getMatterId = useCallback(
    () =>
      activeMatter?.id ||
      activeMatter?.matterId ||
      activeMatter?.Id ||
      "",
    [activeMatter],
  );

  const getCaseTypeName = useCallback(
    () => String(activeMatter?.caseTypeName || "").trim(),
    [activeMatter],
  );

  // The documents API needs the ID token: it carries custom:portalUserId.
  const idToken =
    tokens?.idToken ||
    localStorage.getItem("idToken") ||
    sessionStorage.getItem("idToken") ||
    "";

  const downloadWithFilename = async (url, filename) => {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error("Falha ao baixar o arquivo.");
    const blob = await res.blob();

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = ensurePdfExtension(filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    setRetainerError("");

    const token = getToken();
    const partyId = getPartyId();
    const matterId = getMatterId();
    const caseTypeName = getCaseTypeName();

    if (!token || !partyId || !matterId || !caseTypeName) {
      setRetainerFiles([]);
      setRetainerFilesLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setRetainerFilesLoading(true);
      try {
        const resp = await fetchClientRetainers(
          token,
          partyId,
          matterId,
          caseTypeName,
        );
        if (cancelled) return;

        const nextRetainers = [
          resp?.englishClaimRetainer || null,
          resp?.netherlandsClaimRetainer || null,
        ].filter(Boolean);

        setRetainerFiles(nextRetainers);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch retainer documents:", err);
        setRetainerFiles([]);
      } finally {
        if (!cancelled) setRetainerFilesLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [getCaseTypeName, getMatterId, getPartyId, getToken]);

  const handleDownloadRetainerFile = async (file) => {
    setRetainerError("");

    const token = getToken();
    const partyId = getPartyId();
    const matterId = getMatterId();

    if (!token) {
      setRetainerError("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!partyId) {
      setRetainerError(
        "Não foi possível identificar seu perfil. Tente novamente.",
      );
      return;
    }

    if (!matterId) {
      setRetainerError(
        "Não foi possível identificar seu caso. Tente novamente.",
      );
      return;
    }

    if (!file?.id) {
      setRetainerError(
        "Não foi possível identificar o documento para download.",
      );
      return;
    }

    if (downloadingFileId) return;

    setDownloadingFileId(file.id);

    try {
      const resp = await downloadClientRetainer(
        token,
        partyId,
        matterId,
        file.id,
        file.fileName,
      );
      const url = resp?.signedUrl || resp?.signedUrlV2 || "";

      if (!url) {
        setRetainerError("Não foi possível gerar o link de download.");
        return;
      }

      const desiredName =
        resp?.fileName || file?.fileName || "Contrato de Retenção";
      await downloadWithFilename(url, desiredName);
    } catch (err) {
      console.error("Failed to download retainer file:", err);
      setRetainerError("Não foi possível baixar o documento. Tente novamente.");
    } finally {
      setDownloadingFileId("");
    }
  };

  const shouldShowRetainerSection =
    retainerFilesLoading || retainerFiles.length > 0;

  return (
    <div className="main-wrapper--dashboard">
      <Breadcrumb
        items={[
          { label: "Página Inicial", link: "/dashboard" },
          { label: "Documentos" },
        ]}
      />

      <div className="docsPage">
        <div className="docsHero">
          <h1 className="docsTitle">Documentos</h1>
          <p className="docsSubtitle">
            Aqui você encontra os documentos relacionados ao seu caso.
          </p>
        </div>

        {retainerError ? (
          <div className="docsError" role="alert">
            {retainerError}
          </div>
        ) : null}

        <ClientDocumentsSection
          idToken={idToken}
          partyId={getPartyId()}
          matterId={getMatterId()}
        />

        <div className="docsSectionTitle">Informações sobre o seu caso</div>
        <div className="docsList">
          {docs.map((d) => (
            <section key={d.id} className="docsItem">
              <div className="docsItemLeft">
                <div className="docsItemTitle">{d.title}</div>
              </div>

              <button
                type="button"
                className="docsAction"
                onClick={() => openDoc(d.url)}
                aria-label={`Abrir documento ${d.title}`}
              >
                Visualizar
              </button>
            </section>
          ))}

          {shouldShowRetainerSection ? (
            <>
              <div className="docsSectionTitle">Documentos de Retenção</div>

              {retainerFilesLoading ? (
                <div className="docsMuted">Carregando documentos...</div>
              ) : (
                retainerFiles.map((f) => {
                  const isDownloadingThis = downloadingFileId === f.id;

                  return (
                    <section key={f.id} className="docsItem">
                      <div className="docsItemLeft">
                        <div className="docsItemTitle">
                          {f.fileName || "Documento"}
                        </div>
                        <div className="docsItemMeta">
                          {formatCreatedDate(
                            f.lastModifiedDate || f.createdDate,
                          )}
                          {f.fileSize
                            ? ` • ${formatFileSize(f.fileSize)}`
                            : ""}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="docsAction"
                        onClick={() => handleDownloadRetainerFile(f)}
                        disabled={!!downloadingFileId}
                        aria-label={`Baixar documento ${f.fileName || "Contrato"}`}
                      >
                        {isDownloadingThis ? "Baixando..." : "Baixar"}
                      </button>
                    </section>
                  );
                })
              )}
            </>
          ) : null}
        </div>

        <div className="bottomSpacer" />
        <div className="docsBackWrapper">
          <button
            type="button"
            className="docsBackBtn"
            onClick={() => navigate(-1)}
          >
            Voltar
          </button>
        </div>
      </div>
    </div>
  );
}
